import { fromDate } from '@internationalized/date';
import clsx from 'clsx';
import * as React from 'react';
import {
    CalendarCell,
    CalendarGrid,
    type DateValue,
    Heading,
    RangeCalendar,
} from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';

export type DateRangeValue = {
    from: Date;
    to: Date;
};

export type DateRangePickerProps = {
    /** Value of the range */
    value: DateRangeValue;
    /** Callback when either date changes */
    onChange: (value: DateRangeValue) => void;
    /** Accessible label for the calendar */
    label?: string;
    className?: string;
    disabled?: boolean;
};

/**
 * Select a contiguous range of dates using a single calendar widget.
 */
export function DateRangePicker(props: DateRangePickerProps) {
    const { value, onChange, label = 'Date range', className, disabled } = props;

    const dateRange = React.useMemo(() => {
        return {
            start: fromDate(value.from, 'UTC'),
            end: fromDate(value.to, 'UTC'),
        };
    }, [value.from, value.to]);

    const handleChange = (range: { start: DateValue; end: DateValue }) => {
        onChange({
            from: range.start.toDate('UTC'),
            to: range.end.toDate('UTC'),
        });
    };

    return (
        <RangeCalendar
            aria-label={label}
            isDisabled={disabled}
            value={dateRange}
            onChange={handleChange}
            className={clsx('flex w-full flex-col gap-2 p-3', className)}
        >
            <header className="flex items-center gap-1 px-1">
                <Button
                    slot="previous"
                    className="rounded p-1 hover:bg-action-tertiary"
                    leadingIcon={Icon.ChevronLeft}
                    iconOnly
                    kind="blank"
                    label="Previous month"
                />
                <Heading slot="title" className="flex-1 text-center font-medium text-ui-action" />
                <Button
                    slot="next"
                    className="rounded p-1 hover:bg-action-tertiary"
                    leadingIcon={Icon.ChevronRight}
                    iconOnly
                    kind="blank"
                    label="Next month"
                />
            </header>
            <CalendarGrid className="w-full border-collapse [&_td]:py-0.5">
                {(date) => (
                    <CalendarCell
                        date={date}
                        className="flex size-9 items-center justify-center rounded-full text-ui-base hover:bg-standout-base data-[selected]:rounded-none data-[selection-end]:rounded-r-md data-[selection-start]:rounded-l-md data-[selected]:bg-primary data-[disabled]:text-muted data-[outside-month]:text-muted data-[selected]:text-white data-[focus-visible]:shadow-focus-ring"
                    />
                )}
            </CalendarGrid>
        </RangeCalendar>
    );
}
